import { ReactElement, useEffect, useState } from 'react'
import {
  PageWrapper,
  SectionTitle,
  FlexWrapper,
  DropdownWrapper,
  EditableDropdown,
  Input,
  Button,
  FileUploader,
  Modal,
  Loader
} from '../../../components'
import strings from '../../../locale/en'
import { FetchQuestionWrapper, QuestionsWrapper } from './subcomponents'
import { Span } from '../../../typography'
import SelectQuestions from './SelectQuestions'
import SecondaryButton from 'react-bootstrap/Button'
import CreateTestModal from './CreateSectionModal'
import CreateSectionInit from './CreateSectionInit'
import { useHistory } from 'react-router-dom'
import ROUTES from '../../../const/routes'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getCourses } from '../../../redux/coachingCenter/api'
import { RootState } from '../../../redux/store'
import { getCoursesDropdown } from '../../../helpers'
import { getChildCourses } from '../../../redux/course/api'
import {
  classList,
  difficultyLevelList,
  questionTypeList,
  sortByList
} from './const'
import {
  createTest,
  getQuestionPapers,
  getQuestionPaperTopics
} from '../../../redux/createTest/api'
import { DropdownListProps } from '../../../components/EditableDropdown/typings'
import { resetChapters, resetSubjects } from '../../../redux/course/action'
import { resetQuestionPaperTopics } from '../../../redux/createTest/action'

type CreateTestStep =
  | ''
  | 'FETCH'
  | 'CREATE_SECTION_INIT'
  | 'CREATE_SECTION_START'

const CreateTest = (): ReactElement => {
  const { isLoading, courses, subjects, chapters, questionPaperTopics } =
    useSelector(
      (state: RootState) => ({
        isLoading: state.coachingCenter.isLoading,
        courses: state.coachingCenter.courses,
        subjects: state.course.subjects,
        chapters: state.course.chapters,
        questionPaperTopics: state.createTest.questionPaperTopics
      }),
      shallowEqual
    )

  const initialValues = {
    courseId: '',
    subjectId: '',
    chapterId: '',
    topicId: '',
    questionNumber: '',
    className: '',
    difficultyLevel: '',
    questionType: '',
    sorted: {
      sortBy: 'createdAt',
      ascDesc: 'desc'
    },
    sortBy: ''
  }
  const [values, setValues] = useState(initialValues)

  const {
    menu: {
      admin: {
        createTest: { evaluate, testAddedSuccessfully },
        courseName,
        selectCourse,
        subjectName,
        selectSubject,
        className,
        selectClass,
        selectChapter,
        chapterName,
        selectTopic,
        noOfQuestions,
        enterNoOfQuestions,
        showAdvanceFilter,
        hideAdvanceFilter,
        selectLevel,
        questionType,
        selectQuestion,
        sortBy,
        selectSortBy,
        fetchQuestion,
        or
      }
    },
    test: { difficultyLevel },
    button: { selectAll, proceed, dSelectAll, done }
  } = strings
  const [isAdvanceFilter, setIsAdvanceFilter] = useState(true)
  const [isSelectAll, setIsSelectAll] = useState(false)
  const [currentStep, setCurrentStep] = useState<CreateTestStep>('')
  const coursesList = courses ? getCoursesDropdown(courses) : []
  const subjectList = subjects ? getCoursesDropdown(subjects) : []
  const chapterList = chapters ? getCoursesDropdown(chapters) : []
  const topicsList: Array<DropdownListProps> = questionPaperTopics.map(
    (topic) => ({
      id: topic,
      name: topic
    })
  )
  const questions = [1] // questions fetched from api
  const history = useHistory()
  const dispatch = useDispatch()

  const handleFetchQuestion = () => {
    const searchCriteria: any = {
      courseId: values.courseId,
      subjectId: values.subjectId,
      chapterId: values.chapterId,
      topicId: values.topicId,
      className: values.className
    }

    if (values.difficultyLevel)
      searchCriteria['difficultyLevel'] = values.difficultyLevel

    if (values.questionType)
      searchCriteria['questionType'] = values.questionType

    dispatch(
      getQuestionPapers({
        pageNumber: 1,
        sortBy: values.sorted.sortBy,
        ascDesc: values.sorted.ascDesc,
        pageSizeNumber: Number(values.questionNumber),
        searchCriteria
      })
    )
  }

  const resetState = () => {
    dispatch(resetSubjects())
    dispatch(resetChapters())
    dispatch(resetQuestionPaperTopics())
  }

  useEffect(() => {
    dispatch(getCourses())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return isLoading ? (
    <Loader />
  ) : (
    <PageWrapper id="container">
      <SectionTitle title={evaluate} />
      <FlexWrapper>
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={coursesList}
            title={courseName}
            placeholder={selectCourse}
            handleSelect={({ id }) => {
              resetState()
              dispatch(
                getChildCourses({
                  courseId: id,
                  type: 'SUBJECT'
                })
              )
              setValues({ ...values, courseId: id })
            }}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={subjectList}
            title={subjectName}
            placeholder={selectSubject}
            handleSelect={({ id }) => {
              dispatch(resetChapters())
              dispatch(resetQuestionPaperTopics())
              dispatch(
                getChildCourses({
                  courseId: id,
                  type: 'CHAPTER'
                })
              )
              setValues({ ...values, subjectId: id })
            }}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={classList}
            title={className}
            placeholder={selectClass}
            handleSelect={({ id }) => {
              setValues({ ...values, className: id })
            }}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={chapterList}
            title={chapterName}
            placeholder={selectChapter}
            handleSelect={({ id }) => {
              setValues({ ...values, chapterId: id })
              dispatch(resetQuestionPaperTopics())
              dispatch(
                getQuestionPaperTopics({
                  courseId: values.courseId,
                  subjectId: values.subjectId,
                  chapterId: id
                })
              )
            }}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={topicsList}
            title={selectTopic}
            placeholder={selectTopic}
            handleSelect={({ id }) => {
              setValues({ ...values, topicId: id })
            }}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <Input
            label={noOfQuestions}
            placeholder={enterNoOfQuestions}
            value={values.questionNumber}
            name="questions count"
            onChange={(value) => {
              setValues({ ...values, questionNumber: value })
            }}
            hasPadding={false}
            inputType="number"
          />
        </DropdownWrapper>
      </FlexWrapper>
      <FlexWrapper noPadding>
        <Button onClick={() => setIsAdvanceFilter(!isAdvanceFilter)}>
          {isAdvanceFilter ? showAdvanceFilter : hideAdvanceFilter}
        </Button>
        {!isAdvanceFilter && (
          <>
            <DropdownWrapper>
              <EditableDropdown
                dropdownList={difficultyLevelList}
                title={difficultyLevel}
                placeholder={selectLevel}
                handleSelect={({ id }) => {
                  setValues({ ...values, difficultyLevel: id })
                }}
              />
            </DropdownWrapper>
            <DropdownWrapper>
              <EditableDropdown
                dropdownList={questionTypeList}
                title={questionType}
                placeholder={selectQuestion}
                handleSelect={({ id }) => {
                  setValues({ ...values, questionType: id })
                }}
              />
            </DropdownWrapper>
            <DropdownWrapper>
              <EditableDropdown
                dropdownList={sortByList}
                title={sortBy}
                placeholder={selectSortBy}
                handleSelect={({ id }) => {
                  switch (id) {
                    case 'LEAST USED':
                      setValues({
                        ...values,
                        sortBy: id,
                        sorted: { sortBy: 'useCount', ascDesc: 'desc' }
                      })
                      break
                    case 'MOST USED':
                      setValues({
                        ...values,
                        sortBy: id,
                        sorted: { sortBy: 'useCount', ascDesc: 'asc' }
                      })
                      break
                    default:
                      setValues({
                        ...values,
                        sortBy: id,
                        sorted: { sortBy: 'createdAt', ascDesc: 'desc' }
                      })
                      break
                  }
                }}
              />
            </DropdownWrapper>
          </>
        )}
      </FlexWrapper>
      <FetchQuestionWrapper>
        <Button onClick={handleFetchQuestion}>{fetchQuestion}</Button>
        <Span>{or}</Span>
        <FileUploader
          type="doc"
          onUploadSuccess={(file) => {
            const formData = new FormData()
            formData.append('files', file)
            dispatch(createTest(formData))
          }}
        />
      </FetchQuestionWrapper>
      {questions.length && (
        <>
          <div>
            <SecondaryButton
              variant="outline-secondary"
              onClick={() => setIsSelectAll(!isSelectAll)}
            >
              {isSelectAll ? dSelectAll : selectAll}
            </SecondaryButton>
          </div>
          {questions.map((question) => {
            return (
              <SelectQuestions
                key={`question-${question}`}
                isSelected={isSelectAll}
              />
            )
          })}
          {currentStep === 'FETCH' && (
            <Modal
              handleCancel={() => setCurrentStep('')}
              handleSubmit={() => {
                setCurrentStep('CREATE_SECTION_INIT')
              }}
              title={testAddedSuccessfully}
              description={''}
              submitButtonText={done}
            />
          )}
          {currentStep === 'CREATE_SECTION_INIT' && (
            <CreateSectionInit
              handleCancel={() => setCurrentStep('')}
              handleSubmit={() => {
                setCurrentStep('CREATE_SECTION_START')
              }}
            />
          )}
          {currentStep === 'CREATE_SECTION_START' && (
            <CreateTestModal
              handleCancel={() => setCurrentStep('')}
              handleSubmit={() => {
                setCurrentStep('')
                history.push(ROUTES.TESTSETTING)
              }}
            />
          )}
          <QuestionsWrapper>
            <Button
              disabled={!isSelectAll}
              onClick={() => setCurrentStep('FETCH')}
            >
              {proceed}
            </Button>
          </QuestionsWrapper>
        </>
      )}
    </PageWrapper>
  )
}

export default CreateTest
